import { UserAction } from "@brookbush/types";

import { SignIn, useAuth, useClerk, useSignIn } from "@clerk/nextjs";
import type { AuthenticateWithRedirectParams } from "@clerk/types";
import { Dialog, DialogPanel } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { XIcon } from "lucide-react";
import { useRouter } from "next/compat/router";
import { memo } from "react";

import { useUserAction } from "~/hooks";
import useTrackCTA from "~/hooks/useTrackCTA";

const SignInModal = () => {
	const { userAction, resetUserAction } = useUserAction();
	const isOpen = userAction === UserAction.SIGN_IN;
	const { isLoaded } = useAuth();
	const { signIn } = useSignIn();
	const trackCTA = useTrackCTA();

	const { setUserAction } = useUserAction();

	const router = useRouter();
	const _clerk = useClerk();

	const path = router?.asPath.split("?")[0]!;

	const _signUpWithSocial = async (
		strategy: AuthenticateWithRedirectParams["strategy"],
	) => {
		await signIn?.authenticateWithRedirect({
			strategy,
			redirectUrl: "/sign-in/sso-callback",
			redirectUrlComplete: path,
		});

		resetUserAction();
	};

	return (
		<AnimatePresence mode="wait">
			{isOpen && isLoaded && (
				<Dialog onClose={resetUserAction} open={isOpen && isLoaded} static>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1, transition: { duration: 0.3 } }}
						exit={{ opacity: 0, transition: { duration: 0.2 } }}
						className="fixed inset-0 z-50 bg-black/40 dark:bg-black/50"
						aria-hidden="true"
						key="signin-backdrop"
					/>

					<div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
						<DialogPanel
							as={motion.div}
							key="signup-modal"
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0, transition: { duration: 0.25 } }}
							exit={{ opacity: 0, y: 10, transition: { duration: 0.15 } }}
							className="relative flex w-full max-w-md flex-col gap-6 overflow-hidden rounded-t-xl border bg-white phablet:px-10 px-6 py-8 shadow-black/5 shadow-xl sm:rounded-xl dark:bg-slate-800 dark:shadow-black/30"
						>
							<button
								className="absolute top-3.5 right-4 flex cursor-pointer rounded-full bg-slate-100 p-1 transition-all hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600"
								onClick={resetUserAction}
							>
								<XIcon className="size-4.5" />
							</button>

							<SignIn routing="hash" forceRedirectUrl={path} />

							<p className="text-center">
								Don’t have an account?{" "}
								<button
									onClick={() => {
										trackCTA(UserAction.SIGN_UP, "Sign In Modal");
										setUserAction(UserAction.SIGN_UP);
									}}
									className="text-sky-600 hover:underline dark:text-sky-300"
								>
									Sign Up
								</button>
							</p>
						</DialogPanel>
					</div>
				</Dialog>
			)}
		</AnimatePresence>
	);
};

export default memo(SignInModal);
